import { ArrowLeft, TrendingUp, TrendingDown, Minus, Target, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useProductivityStats } from '@/hooks/useProductivityStats';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface StatsSectionProps {
  onBack: () => void;
}

export function StatsSection({ onBack }: StatsSectionProps) {
  const { stats, loading } = useProductivityStats();

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold">Productivity</h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="h-full flex flex-col">
        <div className="p-4 border-b flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold">Productivity</h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-muted-foreground text-center">
            No data available yet. Start completing tasks to see your stats!
          </p>
        </div>
      </div>
    );
  }

  const TrendIcon = stats.completionTrend === 'up' ? TrendingUp :
                    stats.completionTrend === 'down' ? TrendingDown : Minus;

  const trendColor = stats.completionTrend === 'up' ? 'text-success' :
                     stats.completionTrend === 'down' ? 'text-destructive' : 'text-muted-foreground';

  const focusHours = Math.floor(stats.currentWeek.focusMinutes / 60);
  const focusMins = stats.currentWeek.focusMinutes % 60;
  const adminHours = Math.floor(stats.currentWeek.adminMinutes / 60);
  const adminMins = stats.currentWeek.adminMinutes % 60;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Productivity</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Growth Status */}
        <div className="p-4 rounded-2xl bg-secondary/50 border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Growth Status</h3>
            <div className={cn("flex items-center gap-1", trendColor)}>
              <TrendIcon className="h-5 w-5" />
              <span className="text-sm font-medium capitalize">
                {stats.completionTrend === 'stable' ? 'Stable' : 
                 stats.completionTrend === 'up' ? 'Growing' : 'Declining'}
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {stats.completionTrend === 'up' 
              ? "You're doing better than last week! Keep it up! 🚀"
              : stats.completionTrend === 'down'
              ? "This week has been slower. That's okay—rest is productive too."
              : "Steady progress. Consistency is key! 💪"}
          </p>
        </div>

        {/* Completion Rate */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-success" />
            <h3 className="font-medium">Task Completion</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">This week</span>
              <span className="font-medium">{Math.round(stats.completionRate)}%</span>
            </div>
            <Progress value={stats.completionRate} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {stats.currentWeek.completedTasks} of {stats.currentWeek.totalTasks} tasks completed
            </p>
          </div>
        </div>

        <Separator />

        {/* Main Task Completion */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <h3 className="font-medium">One Thing Focus</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Main tasks completed</span>
              <span className="font-medium">{Math.round(stats.mainTaskCompletionRate)}%</span>
            </div>
            <Progress value={stats.mainTaskCompletionRate} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {stats.currentWeek.mainTasksCompleted} main tasks completed this week
            </p>
          </div>
        </div>

        <Separator />

        {/* Focus vs Admin Hours */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <h3 className="font-medium">Focus Time</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
              <p className="text-xs text-muted-foreground mb-1">Hard Focus</p>
              <p className="text-2xl font-bold text-primary">
                {focusHours}h {focusMins}m
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Making & creating
              </p>
            </div>
            
            <div className="p-4 rounded-xl bg-secondary border">
              <p className="text-xs text-muted-foreground mb-1">Admin</p>
              <p className="text-2xl font-bold">
                {adminHours}h {adminMins}m
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Managing & meetings
              </p>
            </div>
          </div>

          {stats.currentWeek.focusMinutes + stats.currentWeek.adminMinutes > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Focus ratio</span>
                <span className="font-medium">{Math.round(stats.focusRatio)}%</span>
              </div>
              <Progress value={stats.focusRatio} className="h-2" />
            </div>
          )}
        </div>

        <Separator />

        {/* Weekly Trend */}
        <div className="space-y-3">
          <h3 className="font-medium">4-Week Trend</h3>
          <div className="space-y-2">
            {stats.last4Weeks.map((week, i) => {
              const weekLabel = i === 0 ? 'This week' : 
                               i === 1 ? 'Last week' : 
                               format(week.weekStart, 'MMM d');
              const rate = week.totalTasks > 0 
                ? Math.round((week.completedTasks / week.totalTasks) * 100)
                : 0;
              
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground w-20 shrink-0">
                    {weekLabel}
                  </span>
                  <div className="flex-1">
                    <Progress value={rate} className="h-2" />
                  </div>
                  <span className="text-sm font-medium w-10 text-right">{rate}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Daily Breakdown */}
        {stats.dailyCompletions.length > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="font-medium">This Week's Daily Progress</h3>
              <div className="flex gap-1">
                {stats.dailyCompletions.map((day) => {
                  const rate = day.total > 0 ? (day.completed / day.total) : 0;
                  const bgOpacity = rate === 0 ? 0.1 : 0.3 + rate * 0.7;
                  
                  return (
                    <div
                      key={day.date}
                      className="flex-1 aspect-square rounded-lg flex flex-col items-center justify-center text-xs"
                      style={{ 
                        backgroundColor: `hsla(var(--primary) / ${bgOpacity})` 
                      }}
                    >
                      <span className="font-medium">
                        {format(new Date(day.date), 'EEE').slice(0, 1)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {day.completed}/{day.total}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
